import { Col, Row, Select } from "antd";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ResponsivePie } from "@nivo/pie";
import { DashboardCard } from "../../components/DashboardCard";
import { Text } from "../../components/CommonFont/text";
import "./index.css";
import { ResponsiveLine } from "@nivo/line";
import { useGetOrderStatusPieChartQuery } from "../../services/report";
import { useGetExecutivesDropdownFilterQuery } from "../../services/report";
import {useGetExecutiveSalesReportCardsQuery} from "../../services/report";
import {useGetSalesOverviewLineChartQuery} from "../../services/report";
import { useGetProductsDropdownFilterQuery } from "../../services/report";

import DateFilter from "./DateFilter";
import MultiSelectFilter from "../../components/Modals/ReportsModal/MultiSelectFilter";

export const SalesReportDashboard = () => {
  const [selectedExecutives, setSelectedExecutives] = useState<(number | string)[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<(number | string)[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(12, "month"),
    dayjs(),
  ]);
  const isManager = localStorage.getItem("user_role");
  const userId = localStorage.getItem("user_id");

  interface Executive {
    id: string;
    full_name: string;
  }

  interface Product {
    id: string;
    name: string;
  }

  type LineChartSeries = {
    id: string;
    data: {
      x: string;
      y: number;
    }[];
  };

  const [executiveSearch, setExecutiveSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const executiveFilterParams = {
    query: executiveSearch,
    ...(selectedProducts.length > 0 && 
      !selectedProducts.includes("all") && 
      { finished_goods_id: selectedProducts.filter(id => id !== "all").join(",") }),
  };

  // product filter params with executive IDs
  const productFilterParams = {
    query: productSearch,
    ...(selectedExecutives.length > 0 && 
      !selectedExecutives.includes("all") && 
      { executive_id: selectedExecutives.filter(id => id !== "all").join(",") }),
  };

  const { data: executivesResponse, isLoading: execLoading, refetch: refetchExecutives } = useGetExecutivesDropdownFilterQuery(executiveFilterParams);

  const { data: productsData, isLoading: productsLoading, refetch: refetchProducts } = useGetProductsDropdownFilterQuery(productFilterParams);

  // Refetch when filters change
  useEffect(() => {
    refetchExecutives();
  }, [selectedProducts, executiveSearch]);

  useEffect(() => {
    refetchProducts();
  }, [selectedExecutives, productSearch]);

  const executiveOptions = [
    { id: "all", name: "All Executives" },
    ...(executivesResponse?.results?.map((item: Executive) => ({
      id: item.id,
      name: item.full_name,
    })) || []),
  ];

  const productOptions = [
    { id: "all", name: "All Products" },
    ...(productsData?.data?.map((item: Product) => ({
      id: item.id,
      name: item.name,
    })) || []),
  ];

  // Handle executive selection
  const handleExecutiveChange = (values: (number | string)[]) => {
    // If "all" is selected, clear all selections
    if (values.includes("all")) {
      setSelectedExecutives([]);
      setExecutiveSearch("");
    } else {
      setSelectedExecutives(values);
    }
  };

  const handleProductChange = (values: (number | string)[]) => {
    // If "all" is selected, clear all selections
    if (values.includes("all")) {
      setSelectedProducts([]);
      setProductSearch("");
    } else {
      setSelectedProducts(values);
    }
  };

  const startDate = dateRange?.[0]?.format("YYYY-MM-DD") || "";
  const endDate = dateRange?.[1]?.format("YYYY-MM-DD") || "";

  const executiveIdParam = isManager === "2" 
    ? userId || "" 
    : (selectedExecutives.length > 0 && 
      !selectedExecutives.includes("all") && 
      selectedExecutives.filter(id => id !== "all").join(",") || "");
  
  const productIdParam = selectedProducts.length > 0 && 
    !selectedProducts.includes("all") && 
    selectedProducts.filter(id => id !== "all").join(",") || "";

  const { data: dashboardResponse, isLoading: cardLoading } = useGetExecutiveSalesReportCardsQuery({ id: executiveIdParam });
  const dashboardData = dashboardResponse?.data;

  const { data: apiResponse } = useGetOrderStatusPieChartQuery({
    executive_id: isManager === "2" ? "" : executiveIdParam,
    start_date: startDate,
    end_date: endDate,
    finished_goods_id: productIdParam,
  });

  const pieData = apiResponse?.data;
  
  const transformedPieData = pieData
    ? [
        { id: "In Progress", label: "In Progress", value: pieData.in_progress || 0, color: "#26CDEB" },
        { id: "Dispatched", label: "Dispatched", value: pieData.dispatch_added || 0, color: "#B574E0" },
        { id: "Delivered", label: "Delivered", value: pieData.delivered || 0, color: "#1ECD2F" },
        { id: "Completed", label: "Completed", value: pieData.completed || 0, color: "#E53A2F" },
      ]
    : [];


  // Mock data removed and called API for the cards
  const cardData = {
    executive1: {
      count: cardLoading ? "Loading..." : (dashboardData?.total_sales_revenue ?? 0),
      subTitle: "Total Revenue",
      img: "/assets/totalSales.svg",
    },
    executive2: {
      count: cardLoading ? "Loading..." : (dashboardData?.active_orders ?? 0),
      subTitle: "Active Orders",
      img: "/assets/totalSales.svg",
    },
    executive3: {
      count: cardLoading ? "Loading..." : (dashboardData?.quotation_conversion_rate ?? 0),
      subTitle: "Quotation to Order Conversion Rate",
      img: "/assets/totalSales.svg",
    },
    executive4: {
      count: cardLoading ? "Loading..." : (dashboardData?.active_executives ?? 0),
      subTitle: "Active Executives",
      img: "/assets/totalSales.svg",
    },
  };

  const { data: lineChartResponse, isLoading: lineLoading } = useGetSalesOverviewLineChartQuery({
    executive_id: isManager === "2" ? "" : executiveIdParam,
    start_date: startDate,
    end_date: endDate,
    finished_goods_id: productIdParam,
  }, {
      refetchOnMountOrArgChange: true,
    });

  const transformLineChartData = (apiData: any) => {
    if (!apiData) return [];

    const { users, months, data } = apiData;

    return users.map((user: any) => ({
      id: user.name,
      data: months.map((month: string, index: number) => ({
        x: month,
        y: data[user.id]?.[index] ?? 0,
      })),
    }));
  };

  const lineChartData = transformLineChartData(lineChartResponse?.data);  

  const orderStatusData = [
    { id: "In Progress", label: "In Progress", value: 27, color: "#26CDEB" },
    { id: "Dispatched", label: "Dispatched", value: 25, color: "#B574E0" },
    { id: "Delivered", label: "Delivered", value: 18, color: "#1ECD2F" },
    { id: "Completed", label: "Completed", value: 30, color: "#E53A2F" },
  ];

  const productStatusExecutiveData = [
    { id: "P1", label: "P1", value: 45, color: "#26CDEB" },
    { id: "P2", label: "P2", value: 35, color: "#B574E0" },
    { id: "P3", label: "P3", value: 20, color: "#1ECD2F" },
  ];

  const colorArray = ["#26CDEB", "#B574E0", "#1ECD2F", "#E53A2F"];

  const pieChartConfig: any = {
    margin: { top: 60, right: 80, bottom: 80, left: 80 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 3,
    colors: colorArray,
    activeOuterRadiusOffset: 8,
    borderWidth: 1,
    borderColor: {
      from: "color",
      modifiers: [["darker", 0.2]],
    },
    arcLinkLabelsSkipAngle: 10,
    arcLinkLabelsTextColor: "#333333",
    arcLinkLabelsThickness: 2,
    arcLinkLabelsColor: { from: "color" },
    arcLabelsSkipAngle: 10,
    arcLabelsTextColor: {
      from: "color",
      modifiers: [["darker", 2]],
    },
    legends: [
      {
        anchor: "top",
        direction: "row",
        justify: false,
        translateX: 0,
        translateY: -40,
        itemsSpacing: 0,
        itemWidth: 100,
        itemHeight: 18,
        itemTextColor: "#999",
        itemDirection: "left-to-right",
        itemOpacity: 1,
        symbolSize: 18,
        symbolShape: "square",
        effects: [
          {
            on: "hover",
            style: {
              itemTextColor: "#000",
            },
          },
        ],
      },
    ],
  };

  // Colors used for each executive line series
  const lineChartColors = [
    "#e8c1a0",
    "#f47560",
    "#f1e15b",
    "#e8a838",
    "#61cdbb",
    "#97e3d5",
    "#1f77b4",
    "#ff7f0e",
    "#2ca02c",
    "#d62728",
  ];

  const capitalize = (str: string) =>str?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const renderLineChart = (
    <div className="h-400">
      <Text value="Sales Overview" variant="wearlay-headline-2" />
      <div className="chart-container">
        {lineLoading ? (
          <div className="flex justify-center items-center h-full">
            <p>Loading chart...</p>
          </div>
        ) : (
          <div className="chart-area">
            <ResponsiveLine
              data={lineChartData.length > 0 ? lineChartData : []}
              margin={{ top: 20, right: 30, bottom: 70, left: 60 }}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: "auto",
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              colors={lineChartColors}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
                legend: "Month",
                legendOffset: 50,
                legendPosition: "middle",
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Sales",
                legendOffset: -50,
                legendPosition: "middle",
              }}
              pointSize={8}
              pointColor={{ from: "color" }}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointLabelYOffset={-12}
              useMesh={true}
              tooltip={({ point }) => (
                <div
                  style={{
                    background: "white",
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    fontSize: "12px",
                    color: "#333",
                  }}
                >
                  <strong>{point.serieId}</strong>: {point.data.yFormatted}
                </div>
              )}
            />
          </div>
        )}
        {/* Custom Legend - Executive Names */}
        <div className="custom-legend">
          {lineChartData.map((series: LineChartSeries, index: number) => (
            <div key={series.id} className="legend-item">
              <span
                className="legend-dot"
                style={{
                  backgroundColor:
                    lineChartColors[index % lineChartColors.length],
                }}
              />
              <span>{capitalize(series.id)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="sales-report-dashboard mt-24 p-1">
      <div className="mb-4">
        <Text
          value="Sales Report Dashboard"
          variant="wearlay-display"
          className="sales-dashboard-title"
        />
      </div>

      {/* 1st portion: display cards */}
      <div className="flex w-full gap-2 p-1 resp-card mb-24px">
        <DashboardCard
          count={cardData.executive1.count}
          subTitle={cardData.executive1.subTitle}
          img={cardData.executive1.img}
        />
        <DashboardCard
          count={cardData.executive2.count}
          subTitle={cardData.executive2.subTitle}
          img={cardData.executive2.img}
        />
        <DashboardCard
          count={cardData.executive3.count}
          subTitle={cardData.executive3.subTitle}
          img={cardData.executive3.img}
          isPercentage={true}
        />
        {isManager === "1" ? (
          <DashboardCard
            count={cardData.executive4.count}
            subTitle={cardData.executive4.subTitle}
            img={cardData.executive4.img}
          />
        ) : (
          <div style={{ flex: 1 }} />
        )}
      </div>

      <div className="filters-container mb-24px">

        <DateFilter
          dateRange={dateRange}
          setDateRange={setDateRange}
        />

        {isManager === "1" && (
          <MultiSelectFilter
            value={selectedExecutives}
            onChange={handleExecutiveChange}
            options={executiveOptions}
            label="Executive"
            placeholder="Select executives"
            optionLabelKey="name"
            optionValueKey="id"
            marginBottomValue={0}
            dropDownWidth={320}
          />
        )}

        <MultiSelectFilter
          value={selectedProducts}
          onChange={handleProductChange}
          options={productOptions}
          label="Product"
          placeholder="Select Products"
          optionLabelKey="name"
          optionValueKey="id"
          marginBottomValue={0}
          dropDownWidth={320}
        />
      </div>

      {isManager === "2" && (
        <div className="mt-24px mb-24px">
          <Row gutter={[16, 16]}>
            <Col span={24} lg={12}>
              {renderLineChart}
            </Col>
            <Col span={24} lg={12}>
              <div className="h-400">
                <Text value="Order Status" variant="wearlay-headline-2" />
                <div className="h-350">
                  <ResponsivePie
                    data={transformedPieData.length > 0 ? transformedPieData : productStatusExecutiveData}
                    {...pieChartConfig}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* 3rd and 4th portion: both graph in same row */}
      <div className="mt-24px">
        <Row gutter={[16, 16]}>
          {/* 3rd portion: display line chart graph */}
          {isManager !== "2" && (
            <Col span={24} lg={12}>
              {renderLineChart}
            </Col>
          )}
          {/* 4th portion: display round pie chart */}
          {isManager !== "2" && (
            <Col span={24} lg={12}>
              <div className="h-400">
                <Text value="Order Status" variant="wearlay-headline-2" />
                <div className="h-350">
                  <ResponsivePie
                    data={transformedPieData.length > 0 ? transformedPieData : orderStatusData}
                    margin={{ top: 60, right: 80, bottom: 80, left: 80 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    colors={colorArray}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{
                      from: "color",
                      modifiers: [["darker", 0.2]],
                    }}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: "color" }}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{
                      from: "color",
                      modifiers: [["darker", 2]],
                    }}
                    legends={[
                      {
                        anchor: "top",
                        direction: "row",
                        justify: false,
                        translateX: 0,
                        translateY: -40,
                        itemsSpacing: 0,
                        itemWidth: 100,
                        itemHeight: 18,
                        itemTextColor: "#999",
                        itemDirection: "left-to-right",
                        itemOpacity: 1,
                        symbolSize: 18,
                        symbolShape: "square",
                        effects: [
                          {
                            on: "hover",
                            style: {
                              itemTextColor: "#000",
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};
